import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Link2,
  Image as ImageIcon,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Clock,
  BarChart2,
  CheckCircle2,
  Trash2,
  ChevronDown,
} from 'lucide-react';

const AUTHOR_AVATAR =
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80';

const toolbarBtn =
  'w-10 h-10 m-0 flex items-center justify-center p-0 border-0 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-900 hover:text-white transition-colors duration-150';

// Sizes (px) offered in the text size dropdown
const FONT_SIZES = [10, 11, 12, 13, 14, 15, 16, 18, 20, 22, 24, 28, 32, 36, 48, 72];

// Styles for the article body (editor + preview).
// Tailwind's preflight strips default styling from headings, lists, links,
// quotes and code blocks, so these are set explicitly (no typography plugin needed).
const CONTENT_STYLES = [
  'max-w-none text-slate-800 leading-relaxed font-sans',
  '[&_p]:mb-3',
  '[&_h1]:text-4xl [&_h1]:font-extrabold [&_h1]:text-slate-900 [&_h1]:mt-6 [&_h1]:mb-3 [&_h1]:leading-tight',
  '[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-6 [&_h2]:mb-3',
  '[&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-slate-900 [&_h3]:mt-4 [&_h3]:mb-2',
  '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4',
  '[&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4',
  '[&_li]:mb-1',
  '[&_a]:text-blue-600 [&_a]:underline',
  '[&_blockquote]:border-l-4 [&_blockquote]:border-slate-300 [&_blockquote]:pl-4 [&_blockquote]:my-4 [&_blockquote]:italic [&_blockquote]:text-slate-600',
  '[&_pre]:bg-slate-900 [&_pre]:text-emerald-400 [&_pre]:font-mono [&_pre]:text-sm [&_pre]:rounded-xl [&_pre]:p-4 [&_pre]:my-4 [&_pre]:overflow-x-auto',
  '[&_img]:block [&_img]:max-w-full [&_img]:rounded-xl [&_img]:my-4',
].join(' ');

// --- selection helpers (so prompts don't lose the caret) ---
const saveSelection = () => {
  const sel = window.getSelection();
  return sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
};

const restoreSelection = (range) => {
  if (!range) return;
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
};

const escapeAttr = (value) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

function Toolbar({ editorRef, onChange }) {
  // Always make sure the editor has focus before running a command,
  // otherwise document.execCommand silently does nothing.
  const exec = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
  };

  const insertLink = () => {
    const range = saveSelection();
    const url = window.prompt('Enter Link URL:');
    if (!url) return;
    editorRef.current?.focus();
    restoreSelection(range);
    document.execCommand('createLink', false, url);
  };

  // Inserts the image followed by an empty paragraph, so there is always
  // somewhere to keep typing after the image.
  const insertImage = () => {
    const range = saveSelection();
    const url = window.prompt('Enter Image URL:');
    if (!url) return;
    editorRef.current?.focus();
    restoreSelection(range);
    document.execCommand(
      'insertHTML',
      false,
      `<img src="${escapeAttr(url)}" alt="" /><p><br></p>`
    );
  };

  // --- Font size dropdown -------------------------------------------------
  const [currentSize, setCurrentSize] = useState(16);
  const savedRange = useRef(null);

  // Only remember a selection that lives inside the editor
  const saveEditorSelection = () => {
    const range = saveSelection();
    const editor = editorRef.current;
    savedRange.current =
      range && editor && editor.contains(range.commonAncestorContainer) ? range : null;
  };

  // Keep the dropdown showing the size of the text at the caret / selection
  useEffect(() => {
    const onSelectionChange = () => {
      const editor = editorRef.current;
      const sel = window.getSelection();
      if (!editor || !sel || !sel.anchorNode || !editor.contains(sel.anchorNode)) return;
      const node = sel.anchorNode;
      const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
      const size = parseFloat(window.getComputedStyle(el).fontSize);
      if (size) setCurrentSize(Math.round(size));
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [editorRef]);

  // No text selected: set the size for whatever is typed next, like WordPad.
  // A sized span holding an invisible zero-width space is placed at the caret
  // and the caret moves inside it, so new characters pick up the size.
  const applyToCaret = (sel, size) => {
    const editor = editorRef.current;
    const range = sel.getRangeAt(0);
    const container = range.startContainer;
    const el = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;

    const isEmptySizedSpan =
      el !== editor &&
      el.tagName === 'SPAN' &&
      el.style.fontSize &&
      el.textContent.replace(/\u200B/g, '') === '';

    if (isEmptySizedSpan) {
      // Caret already sits in a size placeholder: just change its size
      el.style.fontSize = `${size}px`;
    } else {
      const span = document.createElement('span');
      span.style.fontSize = `${size}px`;
      span.textContent = '\u200B';

      if (el === editor) {
        // Caret is directly in the editor root: give the span a paragraph
        const p = document.createElement('p');
        p.appendChild(span);
        range.insertNode(p);
      } else {
        range.insertNode(span);
      }

      const caret = document.createRange();
      caret.setStart(span.firstChild, 1);
      caret.collapse(true);
      sel.removeAllRanges();
      sel.addRange(caret);
    }

    savedRange.current = sel.getRangeAt(0).cloneRange();
    setCurrentSize(size);
    onChange();
  };

  // Apply an exact px size to the selected text
  const applyFontSize = (size) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    restoreSelection(savedRange.current);

    const sel = window.getSelection();
    if (!sel.rangeCount || !editor.contains(sel.anchorNode)) return;

    // Nothing selected -> applies to the text typed from the caret onward
    if (sel.isCollapsed) {
      applyToCaret(sel, size);
      return;
    }

    // execCommand only supports legacy sizes, so apply the largest one as a
    // marker and then swap those <font> tags for spans with the real px size.
    document.execCommand('styleWithCSS', false, false);
    document.execCommand('fontSize', false, '7');

    const targets = [];
    editor.querySelectorAll('font[size="7"]').forEach((font) => {
      const span = document.createElement('span');
      span.style.fontSize = `${size}px`;
      while (font.firstChild) span.appendChild(font.firstChild);
      font.replaceWith(span);

      // Avoid piling up nested spans when the size is changed repeatedly
      const parent = span.parentElement;
      if (
        parent &&
        parent !== editor &&
        parent.tagName === 'SPAN' &&
        parent.style.fontSize &&
        parent.childNodes.length === 1
      ) {
        parent.style.fontSize = `${size}px`;
        parent.replaceChildren(...span.childNodes);
        targets.push(parent);
      } else {
        targets.push(span);
      }
    });

    if (!targets.length) return;

    // Keep the text selected so the user can try another size
    const range = document.createRange();
    range.setStartBefore(targets[0]);
    range.setEndAfter(targets[targets.length - 1]);
    sel.removeAllRanges();
    sel.addRange(range);
    savedRange.current = range.cloneRange();
    setCurrentSize(size);
    onChange();
  };

  const sizeOptions = FONT_SIZES.includes(currentSize)
    ? FONT_SIZES
    : [...FONT_SIZES, currentSize].sort((a, b) => a - b);

  // The select is wrapped in a box with exactly the same height as the other
  // tool buttons and fills it absolutely, so it always lines up with them.
  const fontSizeSelect = (
    <div key="font-size" className="relative w-16 h-10 m-0 shrink-0" style={{ margin: 0 }}>
      <select
        title="Text size"
        value={currentSize}
        // remember the selection before the dropdown takes focus
        onMouseDown={saveEditorSelection}
        onKeyDown={saveEditorSelection}
        onChange={(e) => applyFontSize(Number(e.target.value))}
        className="absolute inset-0 m-0 w-full h-full appearance-none rounded-lg border-0 bg-slate-100 hover:bg-slate-200 pl-3 pr-6 text-left text-xs font-semibold text-slate-700 cursor-pointer outline-none transition-colors"
      >
        {sizeOptions.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
    </div>
  );

  const tools = [
    { title: 'Bold', Icon: Bold, run: () => exec('bold') },
    { title: 'Italic', Icon: Italic, run: () => exec('italic') },
    { title: 'Underline', Icon: Underline, run: () => exec('underline') },
    { title: 'Strikethrough', Icon: Strikethrough, run: () => exec('strikeThrough') },
    { title: 'Heading 1', Icon: Heading1, run: () => exec('formatBlock', 'h1') },
    { title: 'Heading 2', Icon: Heading2, run: () => exec('formatBlock', 'h2') },
    { title: 'Heading 3', Icon: Heading3, run: () => exec('formatBlock', 'h3') },
    { title: 'Text size', node: fontSizeSelect },
    { title: 'Bullet List', Icon: List, run: () => exec('insertUnorderedList') },
    { title: 'Numbered List', Icon: ListOrdered, run: () => exec('insertOrderedList') },
    { title: 'Insert Link', Icon: Link2, run: insertLink },
    { title: 'Insert Image', Icon: ImageIcon, run: insertImage },
    { title: 'Quote', Icon: Quote, run: () => exec('formatBlock', 'blockquote') },
  ];

  return (
    // justify-between spreads every tool evenly across the full width
    <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md px-4 py-3 rounded-2xl shadow-xl border border-slate-200/80 flex items-center justify-between">
      {tools.map(({ title, Icon, node, run }) =>
        node ? (
          node
        ) : (
          <button
            key={title}
            type="button"
            // keep the editor selection when clicking a tool
            onMouseDown={(e) => e.preventDefault()}
            onClick={run}
            className={toolbarBtn}
            // inline reset: a global `button` margin in the app CSS would push these down
            style={{ margin: 0 }}
            title={title}
          >
            <Icon className="w-4 h-4" />
          </button>
        )
      )}
    </div>
  );
}

function Preview({ postTitle, contentHtml, contentType, featuredImage }) {
  return (
    <div className="w-full max-w-3xl self-start bg-white rounded-3xl p-8 lg:p-12 shadow-2xl border border-slate-200">
      <div className="flex items-center gap-2 mb-6">
        <span className="bg-blue-100 text-blue-600 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
          {contentType.toUpperCase()} PREVIEW
        </span>
        <span className="text-slate-400 text-xs">Target Domain: metacareer.com/blog</span>
      </div>

      {featuredImage && (
        <img
          src={featuredImage}
          alt="Featured Preview"
          className="w-full h-72 object-cover rounded-2xl mb-8 shadow-md"
        />
      )}

      <h1 className="text-3xl font-extrabold text-slate-900 mb-4 leading-tight">
        {postTitle}
      </h1>

      <div className="flex items-center gap-3 mb-8 pb-6 border-b border-slate-100">
        <img src={AUTHOR_AVATAR} className="w-10 h-10 rounded-full" alt="Author" />
        <div>
          <p className="text-sm font-bold text-slate-900">Karol Admin</p>
          <p className="text-xs text-slate-400">
            Published via Meta Career CRM • {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>

      <div
        dangerouslySetInnerHTML={{ __html: contentHtml }}
        className={CONTENT_STYLES}
      />
    </div>
  );
}

export default function ContentEditor({
  activeTab,
  postTitle,
  setPostTitle,
  contentHtml,
  setContentHtml,
  contentType,
  featuredImage,
}) {
  const [wordCount, setWordCount] = useState(0);
  const [readTime, setReadTime] = useState(0);
  // Currently selected image: { el, top, left, width, height } (relative to wrapper)
  const [selectedImg, setSelectedImg] = useState(null);
  const editorRef = useRef(null);
  const wrapperRef = useRef(null);

  // Make Enter create <p> blocks instead of <div>s
  useEffect(() => {
    document.execCommand('defaultParagraphSeparator', false, 'p');
  }, []);

  // The editable area is NOT rendered with dangerouslySetInnerHTML: React
  // would re-write the DOM on re-render and throw the caret back to the
  // start. We write innerHTML ourselves, only when the content changed from
  // outside (AI insert, returning from Preview).
  useLayoutEffect(() => {
    const el = editorRef.current;
    if (el && el.innerHTML !== contentHtml) {
      el.innerHTML = contentHtml;
    }
  }, [contentHtml, activeTab]);

  // Push editor content + stats up after any change
  const syncFromEditor = () => {
    const el = editorRef.current;
    if (!el) return;

    // Never leave an image as the last block: the user needs a line to type on
    if (el.lastElementChild && el.lastElementChild.tagName === 'IMG') {
      el.insertAdjacentHTML('beforeend', '<p><br></p>');
    }

    const text = (el.innerText || '').replace(/\u200B/g, '');
    setWordCount(text.trim().split(/\s+/).filter(Boolean).length);
    setReadTime(text.trim() ? Math.max(1, Math.ceil(text.length / 800)) : 0);
    setContentHtml(el.innerHTML);
  };

  const handleInput = () => {
    setSelectedImg(null);
    syncFromEditor();
  };

  // --- image selection / removal ---
  const handleEditorClick = (e) => {
    if (e.target.tagName === 'IMG' && wrapperRef.current) {
      const imgRect = e.target.getBoundingClientRect();
      const wrapRect = wrapperRef.current.getBoundingClientRect();
      setSelectedImg({
        el: e.target,
        top: imgRect.top - wrapRect.top,
        left: imgRect.left - wrapRect.left,
        width: imgRect.width,
        height: imgRect.height,
      });
    } else {
      setSelectedImg(null);
    }
  };

  const removeSelectedImage = () => {
    if (!selectedImg) return;
    selectedImg.el.remove();
    setSelectedImg(null);
    syncFromEditor();
    editorRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (selectedImg && (e.key === 'Backspace' || e.key === 'Delete')) {
      e.preventDefault();
      removeSelectedImage();
    }
  };

  if (activeTab === 'preview') {
    return (
      <Preview
        postTitle={postTitle}
        contentHtml={contentHtml}
        contentType={contentType}
        featuredImage={featuredImage}
      />
    );
  }

  return (
    <div className="w-full max-w-4xl self-start space-y-6">
      <Toolbar editorRef={editorRef} onChange={syncFromEditor} />

      <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl border border-slate-200/80 min-h-[680px] flex flex-col justify-between">
        <div>
          <textarea
            value={postTitle}
            onChange={(e) => setPostTitle(e.target.value)}
            placeholder="Article or Newsletter Title..."
            rows={2}
            className="w-full text-3xl lg:text-4xl font-extrabold text-slate-900 bg-transparent border-none outline-none resize-none placeholder-slate-300 leading-tight mb-4"
          />

          <div className="flex items-center gap-4 text-xs font-medium text-slate-400 pb-6 mb-6 border-b border-slate-100">
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-blue-500" />
              {readTime} min read
            </span>
            <span>•</span>
            <span className="flex items-center gap-1.5">
              <BarChart2 className="w-3.5 h-3.5 text-purple-500" />
              {wordCount} words
            </span>
          </div>

          <div ref={wrapperRef} className="relative">
            <div
              ref={editorRef}
              data-placeholder="Start writing your post here..."
              contentEditable
              suppressContentEditableWarning
              onInput={handleInput}
              onClick={handleEditorClick}
              onKeyDown={handleKeyDown}
              className={`${CONTENT_STYLES} focus:outline-none min-h-[350px] empty:before:content-[attr(data-placeholder)] empty:before:text-slate-300`}
            />

            {/* Selection frame + remove button for the clicked image */}
            {selectedImg && (
              <div
                className="absolute pointer-events-none rounded-xl border-2 border-blue-500 z-10"
                style={{
                  top: selectedImg.top,
                  left: selectedImg.left,
                  width: selectedImg.width,
                  height: selectedImg.height,
                }}
              >
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={removeSelectedImage}
                  title="Remove image"
                  className="pointer-events-auto absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border-0 bg-red-600 hover:bg-red-500 text-white text-xs font-semibold shadow-lg"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}